import React, { useState, useEffect, useRef } from 'react';

import '../../../../assests/css/admin/AdminInventory/AdminInventory.css';

import Swal from 'sweetalert2';

const AdminInventory = () => {
  // Initial products data
  const [products, setProducts] = useState([
    {
      id: 1,
      name: 'Embroidered Silk Tunic',
      sku: 'WS-102',
      category: 'Women',
      subCategory: 'Stitched',
      price: 89.00,
      stock: 45,
      status: 'in-stock',
      image: '🧥'
    },
    {
      id: 2,
      name: 'Slim Fit Denim Jacket',
      sku: 'MW-042',
      category: 'Men',
      subCategory: 'Western',
      price: 120.00,
      stock: 3,
      status: 'low-stock',
      image: '👔'
    },
    {
      id: 3,
      name: 'Linen Lawn Suit (3pc)',
      sku: 'WU-010',
      category: 'Women',
      subCategory: 'Unstitched',
      price: 55.00,
      stock: 0,
      status: 'out-of-stock',
      image: '👗'
    },
    {
      id: 4,
      name: 'Floral Print Skirt',
      sku: 'KG-089',
      category: 'Kids',
      subCategory: 'Girls',
      price: 35.00,
      stock: 112,
      status: 'in-stock',
      image: '👖'
    },
    {
      id: 5,
      name: 'Canvas Weekend Bag',
      sku: 'AH-022',
      category: 'Accessories',
      subCategory: 'For him',
      price: 75.00,
      stock: 18,
      status: 'in-stock',
      image: '🎒'
    }
  ]);

  const [filteredProducts, setFilteredProducts] = useState(products);
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [selectedSubCategory, setSelectedSubCategory] = useState('All Sub-categories');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showSubCategoryDropdown, setShowSubCategoryDropdown] = useState(false);

  // Refs for dropdown click outside detection
  const categoryDropdownRef = useRef(null);
  const subCategoryDropdownRef = useRef(null);

  // Get unique categories and subcategories
  const categories = ['All Categories', ...new Set(products.map(p => p.category))];
  const subCategories = ['All Sub-categories', ...new Set(products.map(p => p.subCategory))];

  // Calculate statistics
  const totalProducts = products.length;
  const lowStockCount = products.filter(p => p.stock > 0 && p.stock <= 10).length;
  const totalValue = products.reduce((sum, p) => sum + (p.price * p.stock), 0);
  const lastMonthValue = totalValue * 0.988;
  const lastMonthProducts = Math.floor(totalProducts * 0.975);
  const monthGrowth = ((totalProducts - lastMonthProducts) / lastMonthProducts * 100).toFixed(1);
  const valueGrowth = ((totalValue - lastMonthValue) / lastMonthValue * 100).toFixed(1);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
      if (subCategoryDropdownRef.current && !subCategoryDropdownRef.current.contains(event.target)) {
        setShowSubCategoryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close dropdowns on escape key
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setShowCategoryDropdown(false);
        setShowSubCategoryDropdown(false);
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, []);

  // Filter products based on category, subcategory and search
  useEffect(() => {
    let filtered = products;

    if (selectedCategory !== 'All Categories') {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }

    if (selectedSubCategory !== 'All Sub-categories') {
      filtered = filtered.filter(p => p.subCategory === selectedSubCategory);
    }

    if (searchQuery) {
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.sku.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredProducts(filtered);
  }, [selectedCategory, selectedSubCategory, searchQuery, products]);

  // View product details
  const handleView = (product) => {
    Swal.fire({
      title: product.name,
      html: `
        <div class="InVeNtOrY-AdMiN-swal-content">
          <div class="InVeNtOrY-AdMiN-swal-product-icon">${product.image}</div>
          <div class="InVeNtOrY-AdMiN-swal-details">
            <p><strong>SKU:</strong> ${product.sku}</p>
            <p><strong>Category:</strong> ${product.category}</p>
            <p><strong>Sub-category:</strong> ${product.subCategory}</p>
            <p><strong>Price:</strong> $${product.price.toFixed(2)}</p>
            <p><strong>Stock:</strong> ${product.stock} units</p>
            <p><strong>Status:</strong> <span class="InVeNtOrY-AdMiN-status-badge InVeNtOrY-AdMiN-status-${product.status}">${product.status.replace('-', ' ')}</span></p>
          </div>
        </div>
      `,
      icon: 'info',
      confirmButtonText: 'Close',
      confirmButtonColor: '#8b5cf6',
      customClass: {
        popup: 'InVeNtOrY-AdMiN-swal-popup',
        confirmButton: 'InVeNtOrY-AdMiN-swal-button'
      }
    });
  };

  // Edit product
  const handleEdit = async (product) => {
    const { value: formValues } = await Swal.fire({
      title: 'Edit Product',
      html: `
        <div class="InVeNtOrY-AdMiN-swal-form">
          <input id="swal-name" class="swal2-input" placeholder="Product Name" value="${product.name}">
          <input id="swal-sku" class="swal2-input" placeholder="SKU" value="${product.sku}">
          <select id="swal-category" class="swal2-input">
            <option value="Women" ${product.category === 'Women' ? 'selected' : ''}>Women</option>
            <option value="Men" ${product.category === 'Men' ? 'selected' : ''}>Men</option>
            <option value="Kids" ${product.category === 'Kids' ? 'selected' : ''}>Kids</option>
            <option value="Accessories" ${product.category === 'Accessories' ? 'selected' : ''}>Accessories</option>
          </select>
          <input id="swal-subcategory" class="swal2-input" placeholder="Sub-category" value="${product.subCategory}">
          <input id="swal-price" class="swal2-input" type="number" placeholder="Price" value="${product.price}">
          <input id="swal-stock" class="swal2-input" type="number" placeholder="Stock" value="${product.stock}">
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Save Changes',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#8b5cf6',
      cancelButtonColor: '#6b7280',
      customClass: {
        popup: 'InVeNtOrY-AdMiN-swal-popup',
        confirmButton: 'InVeNtOrY-AdMiN-swal-button',
        cancelButton: 'InVeNtOrY-AdMiN-swal-button-cancel'
      },
      preConfirm: () => {
        const name = document.getElementById('swal-name').value;
        const sku = document.getElementById('swal-sku').value;
        const category = document.getElementById('swal-category').value;
        const subCategory = document.getElementById('swal-subcategory').value;
        const price = document.getElementById('swal-price').value;
        const stock = document.getElementById('swal-stock').value;

        if (!name || !sku || !category || !subCategory || !price || !stock) {
          Swal.showValidationMessage('Please fill all fields');
          return false;
        }

        return {
          name,
          sku,
          category,
          subCategory,
          price: parseFloat(price),
          stock: parseInt(stock)
        };
      }
    });

    if (formValues) {
      const updatedStock = formValues.stock;
      const updatedStatus = updatedStock === 0 ? 'out-of-stock' : updatedStock <= 10 ? 'low-stock' : 'in-stock';

      setProducts(products.map(p =>
        p.id === product.id
          ? { ...p, ...formValues, status: updatedStatus }
          : p
      ));

      Swal.fire({
        title: 'Updated!',
        text: 'Product has been updated successfully.',
        icon: 'success',
        confirmButtonColor: '#8b5cf6',
        customClass: {
          popup: 'InVeNtOrY-AdMiN-swal-popup',
          confirmButton: 'InVeNtOrY-AdMiN-swal-button'
        }
      });
    }
  };

  // Delete product
  const handleDelete = (product) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete ${product.name}? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      customClass: {
        popup: 'InVeNtOrY-AdMiN-swal-popup',
        confirmButton: 'InVeNtOrY-AdMiN-swal-button-danger',
        cancelButton: 'InVeNtOrY-AdMiN-swal-button-cancel'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        setProducts(products.filter(p => p.id !== product.id));
        Swal.fire({
          title: 'Deleted!',
          text: 'Product has been deleted successfully.',
          icon: 'success',
          confirmButtonColor: '#8b5cf6',
          customClass: {
            popup: 'InVeNtOrY-AdMiN-swal-popup',
            confirmButton: 'InVeNtOrY-AdMiN-swal-button'
          }
        });
      }
    });
  };

  // Add new product
  const handleAddProduct = async () => {
    const { value: formValues } = await Swal.fire({
      title: 'Add New Product',
      html: `
        <div class="InVeNtOrY-AdMiN-swal-form">
          <input id="swal-name" class="swal2-input" placeholder="Product Name">
          <input id="swal-sku" class="swal2-input" placeholder="SKU">
          <select id="swal-category" class="swal2-input">
            <option value="">Select Category</option>
            <option value="Women">Women</option>
            <option value="Men">Men</option>
            <option value="Kids">Kids</option>
            <option value="Accessories">Accessories</option>
          </select>
          <input id="swal-subcategory" class="swal2-input" placeholder="Sub-category">
          <input id="swal-price" class="swal2-input" type="number" placeholder="Price">
          <input id="swal-stock" class="swal2-input" type="number" placeholder="Stock">
          <input id="swal-emoji" class="swal2-input" placeholder="Emoji (e.g., 👕)" maxlength="2">
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Add Product',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#8b5cf6',
      cancelButtonColor: '#6b7280',
      customClass: {
        popup: 'InVeNtOrY-AdMiN-swal-popup',
        confirmButton: 'InVeNtOrY-AdMiN-swal-button',
        cancelButton: 'InVeNtOrY-AdMiN-swal-button-cancel'
      },
      preConfirm: () => {
        const name = document.getElementById('swal-name').value;
        const sku = document.getElementById('swal-sku').value;
        const category = document.getElementById('swal-category').value;
        const subCategory = document.getElementById('swal-subcategory').value;
        const price = document.getElementById('swal-price').value;
        const stock = document.getElementById('swal-stock').value;
        const emoji = document.getElementById('swal-emoji').value;

        if (!name || !sku || !category || !subCategory || !price || !stock) {
          Swal.showValidationMessage('Please fill all required fields');
          return false;
        }

        return {
          name,
          sku,
          category,
          subCategory,
          price: parseFloat(price),
          stock: parseInt(stock),
          image: emoji || '📦'
        };
      }
    });

    if (formValues) {
      const newProduct = {
        id: Math.max(...products.map(p => p.id), 0) + 1,
        ...formValues,
        status: formValues.stock === 0 ? 'out-of-stock' : formValues.stock <= 10 ? 'low-stock' : 'in-stock'
      };

      setProducts([...products, newProduct]);

      Swal.fire({
        title: 'Added!',
        text: 'Product has been added successfully.',
        icon: 'success',
        confirmButtonColor: '#8b5cf6',
        customClass: {
          popup: 'InVeNtOrY-AdMiN-swal-popup',
          confirmButton: 'InVeNtOrY-AdMiN-swal-button'
        }
      });
    }
  };

  return (
    <div className="InVeNtOrY-AdMiN-container">
      {/* Header */}
      <div className="InVeNtOrY-AdMiN-header">
        <div className="InVeNtOrY-AdMiN-breadcrumb">
          <span className="InVeNtOrY-AdMiN-breadcrumb-item">Inventory</span>
          <span className="InVeNtOrY-AdMiN-breadcrumb-separator">/</span>
          <span className="InVeNtOrY-AdMiN-breadcrumb-item InVeNtOrY-AdMiN-active">Overview</span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="InVeNtOrY-AdMiN-stats-grid">
        <div className="InVeNtOrY-AdMiN-stat-card">
          <div className="InVeNtOrY-AdMiN-stat-content">
            <div className="InVeNtOrY-AdMiN-stat-header">
              <span className="InVeNtOrY-AdMiN-stat-title">TOTAL PRODUCTS</span>
              <div className="InVeNtOrY-AdMiN-stat-icon InVeNtOrY-AdMiN-stat-icon-blue">
                📦
              </div>
            </div>
            <div className="InVeNtOrY-AdMiN-stat-value">{totalProducts.toLocaleString()}</div>
            <div className="InVeNtOrY-AdMiN-stat-footer">
              <span className="InVeNtOrY-AdMiN-stat-badge InVeNtOrY-AdMiN-stat-badge-success">
                +{monthGrowth}% this month
              </span>
            </div>
          </div>
        </div>

        <div className="InVeNtOrY-AdMiN-stat-card">
          <div className="InVeNtOrY-AdMiN-stat-content">
            <div className="InVeNtOrY-AdMiN-stat-header">
              <span className="InVeNtOrY-AdMiN-stat-title">LOW STOCK ALERTS</span>
              <div className="InVeNtOrY-AdMiN-stat-icon InVeNtOrY-AdMiN-stat-icon-warning">
                ⚠️
              </div>
            </div>
            <div className="InVeNtOrY-AdMiN-stat-value">{lowStockCount}</div>
            <div className="InVeNtOrY-AdMiN-stat-footer">
              <span className="InVeNtOrY-AdMiN-stat-badge InVeNtOrY-AdMiN-stat-badge-danger">
                Needs attention
              </span>
            </div>
          </div>
        </div>

        <div className="InVeNtOrY-AdMiN-stat-card">
          <div className="InVeNtOrY-AdMiN-stat-content">
            <div className="InVeNtOrY-AdMiN-stat-header">
              <span className="InVeNtOrY-AdMiN-stat-title">TOTAL VALUE</span>
              <div className="InVeNtOrY-AdMiN-stat-icon InVeNtOrY-AdMiN-stat-icon-purple">
                💰
              </div>
            </div>
            <div className="InVeNtOrY-AdMiN-stat-value">${totalValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            <div className="InVeNtOrY-AdMiN-stat-footer">
              <span className="InVeNtOrY-AdMiN-stat-badge InVeNtOrY-AdMiN-stat-badge-success">
                +{valueGrowth}% vs last month
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Product Inventory Section */}
      <div className="InVeNtOrY-AdMiN-main-card">
        <div className="InVeNtOrY-AdMiN-card-header">
          <h2 className="InVeNtOrY-AdMiN-card-title">Product Inventory</h2>
          <div className="InVeNtOrY-AdMiN-card-actions">
            {/* Category Dropdown */}
            <div className="InVeNtOrY-AdMiN-dropdown" ref={categoryDropdownRef}>
              <button
                className="InVeNtOrY-AdMiN-dropdown-button"
                onClick={() => {
                  setShowCategoryDropdown(!showCategoryDropdown);
                  setShowSubCategoryDropdown(false);
                }}
              >
                <span className="InVeNtOrY-AdMiN-dropdown-icon">🏷️</span>
                {selectedCategory}
                <span className="InVeNtOrY-AdMiN-dropdown-arrow">▼</span>
              </button>
              {showCategoryDropdown && (
                <div className="InVeNtOrY-AdMiN-dropdown-menu">
                  {categories.map(cat => (
                    <div
                      key={cat}
                      className={`InVeNtOrY-AdMiN-dropdown-item ${selectedCategory === cat ? 'InVeNtOrY-AdMiN-active' : ''}`}
                      onClick={() => {
                        setSelectedCategory(cat);
                        setShowCategoryDropdown(false);
                      }}
                    >
                      {cat}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Sub-category Dropdown */}
            <div className="InVeNtOrY-AdMiN-dropdown" ref={subCategoryDropdownRef}>
              <button
                className="InVeNtOrY-AdMiN-dropdown-button"
                onClick={() => {
                  setShowSubCategoryDropdown(!showSubCategoryDropdown);
                  setShowCategoryDropdown(false);
                }}
              >
                <span className="InVeNtOrY-AdMiN-dropdown-icon">📋</span>
                {selectedSubCategory}
                <span className="InVeNtOrY-AdMiN-dropdown-arrow">▼</span>
              </button>
              {showSubCategoryDropdown && (
                <div className="InVeNtOrY-AdMiN-dropdown-menu">
                  {subCategories.map(subCat => (
                    <div
                      key={subCat}
                      className={`InVeNtOrY-AdMiN-dropdown-item ${selectedSubCategory === subCat ? 'InVeNtOrY-AdMiN-active' : ''}`}
                      onClick={() => {
                        setSelectedSubCategory(subCat);
                        setShowSubCategoryDropdown(false);
                      }}
                    >
                      {subCat}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add Product Button */}
            <button className="InVeNtOrY-AdMiN-add-button" onClick={handleAddProduct}>
              <span className="InVeNtOrY-AdMiN-add-icon">➕</span>
              Add Product
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="InVeNtOrY-AdMiN-search-bar">
          <span className="InVeNtOrY-AdMiN-search-icon">🔍</span>
          <input
            type="text"
            className="InVeNtOrY-AdMiN-search-input"
            placeholder="Search products by name or SKU..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Products Table */}
        <div className="InVeNtOrY-AdMiN-table-container">
          <table className="InVeNtOrY-AdMiN-table">
            <thead className="InVeNtOrY-AdMiN-table-header">
              <tr>
                <th className="InVeNtOrY-AdMiN-table-th">PRODUCT</th>
                <th className="InVeNtOrY-AdMiN-table-th">SKU</th>
                <th className="InVeNtOrY-AdMiN-table-th">CATEGORY</th>
                <th className="InVeNtOrY-AdMiN-table-th">SUB-CATEGORY</th>
                <th className="InVeNtOrY-AdMiN-table-th">PRICE</th>
                <th className="InVeNtOrY-AdMiN-table-th">STOCK</th>
                <th className="InVeNtOrY-AdMiN-table-th">STATUS</th>
                <th className="InVeNtOrY-AdMiN-table-th">ACTIONS</th>
              </tr>
            </thead>
            <tbody className="InVeNtOrY-AdMiN-table-body">
              {filteredProducts.map((product, index) => (
                <tr key={product.id} className="InVeNtOrY-AdMiN-table-row" style={{ animationDelay: `${index * 0.05}s` }}>
                  <td className="InVeNtOrY-AdMiN-table-td">
                    <div className="InVeNtOrY-AdMiN-product-cell">
                      <span className="InVeNtOrY-AdMiN-product-image">{product.image}</span>
                      <span className="InVeNtOrY-AdMiN-product-name">{product.name}</span>
                    </div>
                  </td>
                  <td className="InVeNtOrY-AdMiN-table-td">{product.sku}</td>
                  <td className="InVeNtOrY-AdMiN-table-td">{product.category}</td>
                  <td className="InVeNtOrY-AdMiN-table-td">
                    <span className="InVeNtOrY-AdMiN-subcategory-badge">{product.subCategory}</span>
                  </td>
                  <td className="InVeNtOrY-AdMiN-table-td InVeNtOrY-AdMiN-price">${product.price.toFixed(2)}</td>
                  <td className="InVeNtOrY-AdMiN-table-td InVeNtOrY-AdMiN-stock">{product.stock}</td>
                  <td className="InVeNtOrY-AdMiN-table-td">
                    <span className={`InVeNtOrY-AdMiN-status-badge InVeNtOrY-AdMiN-status-${product.status}`}>
                      {product.status === 'in-stock' && 'In Stock'}
                      {product.status === 'low-stock' && 'Low Stock'}
                      {product.status === 'out-of-stock' && 'Out of Stock'}
                    </span>
                  </td>
                  <td className="InVeNtOrY-AdMiN-table-td">
                    <div className="InVeNtOrY-AdMiN-action-buttons">
                      <button
                        className="InVeNtOrY-AdMiN-action-button InVeNtOrY-AdMiN-action-view"
                        onClick={() => handleView(product)}
                        title="View"
                      >
                        👁️
                      </button>
                      <button
                        className="InVeNtOrY-AdMiN-action-button InVeNtOrY-AdMiN-action-edit"
                        onClick={() => handleEdit(product)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        className="InVeNtOrY-AdMiN-action-button InVeNtOrY-AdMiN-action-delete"
                        onClick={() => handleDelete(product)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredProducts.length === 0 && (
            <div className="InVeNtOrY-AdMiN-empty-state">
              <div className="InVeNtOrY-AdMiN-empty-icon">📦</div>
              <div className="InVeNtOrY-AdMiN-empty-text">No products found</div>
              <div className="InVeNtOrY-AdMiN-empty-subtext">Try adjusting your filters or search query</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


export default AdminInventory;